import { Component, Inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators, FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import * as dayjs from 'dayjs';
import { CurrencyConverterPurePipe } from '../../currency-converter-pure.pipe';
import { CompetitorService } from './competitor.service';
import { NotificationService } from '../../notifications/notification.service';

export interface CompetitorDialogData {
  hotel: {
    title: string;
    starRating: any;
    location: string;
    rate: number;
    hotelId: string;
  };
  room: {
    roomType: string;
    boardName: string;
    rateClass: 'R' | 'NR';
    netPriceProf: number;
    taxesExcluded: number;
    optionNightsNetTotalProf: number;
    pricingCurrency: string;
    occupancyRoom: number;
    adults: number;
    childrenCount: number;
    childrenAges: number[];
    roomCode: string;
  };
  dates: {
    arrivalDate: Date | string;
    departureDate: Date | string;
  };
  payload: {
    city: string;
    nationality?: string;
  };
  clientSetting: any;
  currencies: any[];
  currencyAgency: any;
  agent_id?: string | number | null;
  agency_id?: string | number | null;
  file_id?: string | number | null;
}

@Component({
  selector: 'usb-competitor',
  templateUrl: './competitor.component.html',
  styleUrls: ['./competitor.component.scss'],
  providers: [CurrencyConverterPurePipe]
})
export class CompetitorComponent implements OnInit {
  competitorForm: FormGroup;
  loading = false;
  /** Nacionalidad editable del modal (independiente de data.payload.nationality). */
  nationality: string = '';

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: CompetitorDialogData,
    private dialogRef: MatDialogRef<CompetitorComponent>,
    private formBuilder: FormBuilder,
    private competitorService: CompetitorService,
    private notificationService: NotificationService,
    private currencyPipe: CurrencyConverterPurePipe
  ) {}

  ngOnInit(): void {
    this.nationality = this.data.payload?.nationality || '';
    this.competitorForm = this.formBuilder.group({
      competitorUrls: this.formBuilder.array([
        new FormControl(null, [Validators.pattern(/^https?:\/\/.+/)])
      ]),
      competitorImages: this.formBuilder.array([]),
      foundPrice: new FormControl(null),
      foundCurrency: new FormControl('USD'),
      foundHotel: new FormControl(null),
      foundNote: new FormControl(''),
      comments: new FormControl('')
    });
  }

  /**
   * Array de URLs de competencia (FormArray).
   */
  get competitorUrlsArray(): FormArray {
    return this.competitorForm.get('competitorUrls') as FormArray;
  }

  /**
   * Array de imágenes de competencia (FormArray de FormControl<string>).
   */
  get competitorImagesArray(): FormArray {
    return this.competitorForm.get('competitorImages') as FormArray;
  }

  /**
   * Getter del FormControl de comentarios generales.
   * Se mantiene como getter (no como propiedad) para que el binding del template
   * se reevalúe cuando Angular actualice el form.
   */
  get commentsControl(): FormControl {
    return this.competitorForm.get('comments') as FormControl;
  }

  /**
   * Indica si se puede agregar otra URL (siempre true, deshabilitado solo si está cargando).
   */
  get canAddUrl(): boolean {
    return true;
  }

  /**
   * Calcula la cantidad de noches entre arrivalDate y departureDate.
   */
  get nights(): number {
    const arrival = dayjs(this.data.dates.arrivalDate);
    const departure = dayjs(this.data.dates.departureDate);
    return departure.diff(arrival, 'day');
  }

  /**
   * Agrega un nuevo FormControl de URL al FormArray.
   */
  addCompetitorUrl(): void {
    this.competitorUrlsArray.push(
      new FormControl(null, [Validators.pattern(/^https?:\/\/.+/)])
    );
  }

  /**
   * Quita una URL del FormArray por índice, solo si hay más de una.
   */
  removeCompetitorUrl(i: number): void {
    if (this.competitorUrlsArray.length > 1) {
      this.competitorUrlsArray.removeAt(i);
    }
  }

  /**
   * Handler del input de selección de imágenes.
   * Valida tamaño (5 MB máx) y tipo (image/*), convierte a base64 y pushea al FormArray.
   * Si una imagen falla, notifica error y la omite.
   */
  onCompetitorImagesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const files = Array.from(input.files);
    const imagesArray = this.competitorForm.get('competitorImages') as FormArray;
    const MAX_BYTES = 5 * 1024 * 1024;

    files.forEach((file) => {
      if (!file.type.startsWith('image/')) {
        this.notificationService.warning(`${file.name} no es una imagen válida`);
        return;
      }
      if (file.size > MAX_BYTES) {
        this.notificationService.warning(`${file.name} supera el límite de 5 MB`);
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const commaIndex = result.indexOf(',');
        const base64 = commaIndex >= 0 ? result.substring(commaIndex + 1) : result;
        imagesArray.push(new FormControl(base64));
      };
      reader.readAsDataURL(file);
    });

    // Limpia el input para permitir re-seleccionar el mismo archivo si fue removido.
    input.value = '';
  }

  /**
   * Quita una imagen del FormArray por índice.
   */
  removeCompetitorImage(i: number): void {
    const imagesArray = this.competitorForm.get('competitorImages') as FormArray;
    if (i >= 0 && i < imagesArray.length) {
      imagesArray.removeAt(i);
    }
  }

  /**
   * Arma el payload con los datos del formulario y lo envía via CompetitorService.
   * El payload sigue el formato de /api/v1/rate-competitions.
   * En caso de éxito cierra el modal; si falla, muestra notificación de error.
   */
  onSave(): void {
    this.loading = true;

    const raw = this.competitorForm.value;
    const urls = (raw.competitorUrls as (string | null)[]) || [];
    const images = (raw.competitorImages as string[]) || [];

    // Filtrar URLs no vacías
    const filteredUrls = urls.filter((u: any): u is string => typeof u === 'string' && u.trim() !== '');

    // Armar el payload. Como el modelo React ya tiene `foundPrice`/`foundCurrency`/`foundHotel`
    // arriba, las URLs NO llevan precio inline — todas comparten el mismo precio encontrado.
    const sharedPrice = raw.foundPrice !== null && raw.foundPrice !== undefined && raw.foundPrice !== ''
      ? { amount: Number(raw.foundPrice), currency: raw.foundCurrency || 'USD' }
      : null;

    const competitors = filteredUrls.map((url: string, i: number) => ({
      url,
      price: sharedPrice ? sharedPrice.amount : null,
      currency: sharedPrice ? sharedPrice.currency : null,
      hotelName: raw.foundHotel || null,
      images: i === 0 ? images : []
    }));

    const payload = {
      hotelId: String(this.data.hotel?.hotelId ?? ''),
      roomCode: this.data.room?.roomCode || '',
      city: this.data.payload?.city || '',
      nationality: this.nationality || null,
      dates: {
        arrivalDate: this.formatDate(this.data.dates?.arrivalDate),
        departureDate: this.formatDate(this.data.dates?.departureDate),
      },
      competitors,
      note: raw.foundNote || null,
      comments: raw.comments || null,
      agent_id: this.data.agent_id ?? null,
      agency_id: this.data.agency_id ?? null,
      file_id: this.data.file_id ?? null
    };

    this.competitorService.createCompetitorReport(payload).subscribe({
      next: () => {
        this.loading = false;
        this.notificationService.success('Reporte de disparidad guardado correctamente');
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.loading = false;
        this.notificationService.error('Error al guardar el reporte. Intenta nuevamente.');
        console.error('[CompetitorComponent] Error al guardar:', err);
      }
    });
  }

  /**
   * Cierra el modal sin guardar.
   */
  onCancel(): void {
    this.dialogRef.close();
  }

  /**
   * Serializa una fecha (Date o string) a formato YYYY-MM-DD usando dayjs.
   */
  private formatDate(d: Date | string | undefined | null): string {
    if (!d) return '';
    return dayjs(d).format('YYYY-MM-DD');
  }
}
